import { LABEL_COLORS, createLabelSchema, type LabelColor, type LabelDto } from '@tms/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  Field,
  Input,
  LabelDot,
  SelectMenu,
  SelectMenuItem,
} from '../../components/ui';
import { useEnumLabels, useErrorText, useT } from '../../i18n';
import { useCreateLabel, useUpdateLabel } from './api';

interface LabelValues {
  name: string;
  color: LabelColor;
}

export interface LabelFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectCode: string;
  /** Set when editing an existing label. */
  label?: LabelDto | undefined;
}

export const LabelFormDialog = ({
  open,
  onOpenChange,
  projectCode,
  label,
}: LabelFormDialogProps) => {
  const { t } = useT();
  const { labelColor } = useEnumLabels();
  const { errorText, applyApiError } = useErrorText();
  const isEdit = label !== undefined;
  const create = useCreateLabel(projectCode);
  const update = useUpdateLabel(projectCode);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LabelValues>({
    resolver: zodResolver(createLabelSchema),
    defaultValues: { name: '', color: 'gray' },
  });

  useEffect(() => {
    if (!open) return;
    reset({ name: label?.name ?? '', color: label?.color ?? 'gray' });
  }, [open, label, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isEdit) {
        await update.mutateAsync({ labelId: label.id, input: values });
        toast.success(t('labels.updated', { name: values.name }));
      } else {
        await create.mutateAsync(values);
        toast.success(t('labels.created', { name: values.name }));
      }
      onOpenChange(false);
    } catch (error) {
      if (!applyApiError(error, setError, ['name', 'color'])) {
        toast.error(errorText(error, 'labels.saveFailed'));
      }
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('labels.editTitle') : t('labels.new')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => void onSubmit()} loading={isSubmitting}>
            {isEdit ? t('common.saveChanges') : t('labels.create')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field label={t('common.name')} error={errors.name?.message} required>
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              invalid={invalid}
              aria-describedby={describedBy}
              autoFocus
              placeholder={t('labels.newPlaceholder')}
              {...register('name')}
            />
          )}
        </Field>

        <Field label={t('labels.color')} error={errors.color?.message}>
          {({ id, invalid, describedBy }) => (
            <Controller
              control={control}
              name="color"
              render={({ field }) => (
                <SelectMenu
                  id={id}
                  invalid={invalid}
                  aria-describedby={describedBy}
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  {LABEL_COLORS.map((color) => (
                    <SelectMenuItem key={color} value={color}>
                      <span className="flex items-center gap-2">
                        <LabelDot color={color} />
                        {labelColor(color)}
                      </span>
                    </SelectMenuItem>
                  ))}
                </SelectMenu>
              )}
            />
          )}
        </Field>

        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
};
